import { useEffect, useState } from 'react';
import type { Meaning } from '@smysly/shared';
import { sound } from '../lib/sound.js';
import { haptic } from '../lib/telegram.js';

export function Inspector({
  meaning,
  meanings,
  releasedEnergy,
  haptics,
  onClose,
  onUpdate,
  onPop,
  onRemoveShell,
  onSplit,
  onMerge,
  onTransfer,
}: {
  meaning: Meaning;
  meanings: Meaning[];
  releasedEnergy: number;
  haptics: boolean;
  onClose(): void;
  onUpdate(patch: Record<string, unknown>): Promise<void>;
  onPop(): Promise<void>;
  onRemoveShell(): Promise<void>;
  onSplit(titles: [string, string]): Promise<void>;
  onMerge(targetId: string): Promise<void>;
  onTransfer(): Promise<void>;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState<'pop' | 'shell' | null>(null);
  const [split, setSplit] = useState(false);
  const [titles, setTitles] = useState<[string, string]>([meaning.title, '']);
  const [mergeTarget, setMergeTarget] = useState('');
  useEffect(() => { setConfirming(null); setSplit(false); setTitles([meaning.title, '']); }, [meaning.id]);
  const activeShell = meaning.shell && meaning.shell.status !== 'removed' ? meaning.shell : null;

  const pop = async () => {
    if (confirming !== 'pop') return setConfirming('pop');
    sound.play('bubble_pop');
    haptic('pop', haptics);
    await onPop();
  };
  const shellPop = async () => {
    if (confirming !== 'shell') return setConfirming('shell');
    sound.play('shell_pop');
    haptic('success', haptics);
    await onRemoveShell();
    setConfirming(null);
  };

  return (
    <aside className="inspector" aria-label={`Смысл ${meaning.title}`}>
      <div className="inspector__top">
        <div>
          <p className="eyebrow">Шар смысла</p>
          <h2>{meaning.title}</h2>
        </div>
        <button className="icon-button" onClick={onClose} aria-label="Закрыть">×</button>
      </div>

      <div className="life-control">
        <div><span>Место в жизни</span><strong>{meaning.importance}</strong></div>
        <div className="life-control__buttons">
          <button onClick={() => void onUpdate({ importance: Math.max(0, meaning.importance - 1) })} disabled={meaning.importance <= 0}>−</button>
          <div className="life-control__track"><i style={{ width: `${meaning.importance}%` }} /></div>
          <button onClick={() => void onUpdate({ importance: Math.min(100, meaning.importance + 1), releasedEnergySpend: 1 })} disabled={releasedEnergy <= 0 || meaning.importance >= 100}>+</button>
        </div>
      </div>

      <div className="quality-row">
        <span><i style={{ width: `${meaning.realization}%` }} />Наполненность</span>
        <span><i style={{ width: `${meaning.vitality}%` }} />Живая энергия</span>
      </div>

      {activeShell && (
        <section className="shell-card">
          <span className="shell-card__orb" />
          <div>
            <p className="eyebrow">Возможная оболочка</p>
            <h3>{activeShell.title}</h3>
            <p>Похоже, здесь может быть внешний или компенсаторный мотив. Решение остаётся за вами.</p>
            <button className={confirming === 'shell' ? 'button button--danger' : 'button button--ghost'} onClick={() => void shellPop()}>
              {confirming === 'shell' ? 'Подтвердить и освободить' : 'Снять оболочку'}
            </button>
          </div>
        </section>
      )}

      {releasedEnergy > 0 && (
        <button className="button button--energy" onClick={() => { sound.play('energy_transfer'); void onTransfer(); }}>
          Наполнить из освобождённой энергии
        </button>
      )}

      <details className="transform-tools">
        <summary>Исследовать форму</summary>
        <button className="text-button" onClick={() => setSplit(!split)}>Разделить на два смысла</button>
        {split && (
          <div className="split-form">
            <input value={titles[0]} onChange={(event) => setTitles([event.target.value, titles[1]])} maxLength={48} aria-label="Первый смысл" />
            <input value={titles[1]} onChange={(event) => setTitles([titles[0], event.target.value])} maxLength={48} placeholder="Второй смысл" aria-label="Второй смысл" />
            <button disabled={!titles[0].trim() || !titles[1].trim()} onClick={() => { sound.play('bubble_split'); void onSplit([titles[0].trim(), titles[1].trim()]); }}>Разделить</button>
          </div>
        )}
        {meanings.length > 1 && (
          <div className="merge-form">
            <select value={mergeTarget} onChange={(event) => setMergeTarget(event.target.value)} aria-label="С чем соединить">
              <option value="">Соединить с…</option>
              {meanings.filter((candidate) => candidate.id !== meaning.id && candidate.status !== 'popped').map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.title}</option>)}
            </select>
            <button disabled={!mergeTarget} onClick={() => { sound.play('bubble_merge'); haptic('merge', haptics); void onMerge(mergeTarget); }}>Соединить</button>
          </div>
        )}
      </details>

      <button className={confirming === 'pop' ? 'button button--danger inspector__pop' : 'text-button inspector__pop'} onClick={() => void pop()}>
        {confirming === 'pop' ? `Лопнуть и освободить ${meaning.importance} единиц` : 'Лопнуть этот смысл'}
      </button>
    </aside>
  );
}
